import { makeStyles } from '@material-ui/styles';
import { differenceInMinutes } from 'date-fns';

import { Text, MainButton } from '@bizzabo/design-system';

import ScheduleModal from '../../components/ScheduleModal';
import { ScheduledMeeting } from '../../types';
import useTypeTracking from '../../../../analytics';
import { useMeetings } from '../../MeetingsTool/MeetingsToolContext';
import useDeclineMeeting from '../../hooks/useDeclineMeeting';
import LoaderSpinner from '../../../../people/components/LoaderSpinner';

interface DeclineInvitationModalProps {
    isOpen: boolean;
    meeting: ScheduledMeeting;
    closeModal: () => void;
}

const DeclineInvitationModal: React.FC<DeclineInvitationModalProps> = ({
    isOpen,
    meeting,
    closeModal,
}) => {
    const classes = useStyles();
    const durationInMinutes = differenceInMinutes(meeting.endDate, meeting.startDate);
    const { eventId } = useMeetings();
    const declineMeeting = useDeclineMeeting();

    const { trackClickEvent, Track } = useTypeTracking({
        data: {
            description: {
                meeting_id: meeting.id,
                locationType: meeting.locationType,
                meeting_length: durationInMinutes,
                number_of_invitees: meeting.participants.length - 1,
                invitation_text: meeting.description,
            },
        },
    });

    const onCloseModal = () => {
        closeModal();
        trackClickEvent({
            subCategory: 'MEETING_DECLINED_CLOSED',
        });
    };

    const onDecline = () => {
        declineMeeting.mutate(
            {
                eventId,
                meetingId: meeting.id,
            },
            {
                onSuccess: () => {
                    closeModal();
                },
            }
        );
        trackClickEvent({
            subCategory: 'MEETING_DECLINED_APPROVED',
        });
    };

    return (
        <Track>
            <ScheduleModal isOpen={isOpen} closeModal={onCloseModal}>
                <Text variant="t50" className={classes.titleModal}>
                    Are you sure you want to cancel this invitation?
                </Text>
                <MainButton
                    onClick={onDecline}
                    variant="fill"
                    before={declineMeeting.isLoading && <LoaderSpinner />}
                >
                    {declineMeeting.isLoading ? 'Canceling' : 'Yes, Cancel'}
                </MainButton>
            </ScheduleModal>
        </Track>
    );
};

const useStyles = makeStyles(
    () => ({
        titleModal: {
            marginBottom: 24,
            textAlign: 'center',
        },
    }),
    { classNamePrefix: 'meetings-decline-invitation-modal' }
);

export default DeclineInvitationModal;
